import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Search, ShoppingCart } from "lucide-react";

import { BackToTop } from "@/components/shop/BackToTop";
import { CardGrid } from "@/components/shop/CardGrid";
import { CardDetailModal } from "@/components/shop/CardDetailModal";
import { ShopFilters } from "@/components/shop/ShopFilters";
import { ShopHero } from "@/components/shop/ShopHero";
import { Ticker } from "@/components/shop/Ticker";
import {
  type FilterOptions,
  type ShopFilterState,
  type ShopRarity,
} from "@/features/shopFilters";
import {
  fetchStorefrontBundles,
  fetchStorefrontFacets,
  fetchStorefrontFeed,
  fetchStorefrontSold,
  type StorefrontBundle,
  type StorefrontFacetsResponse,
  type StorefrontFeedFilters,
  type StorefrontSort,
} from "@/features/storefront";
import {
  filtersToSearch,
  parseShopUrlSearch,
  searchToFilters,
  type ShopUrlSearch,
} from "@/features/shopUrlState";
import { useStorefrontFeed } from "@/features/useStorefrontFeed";

import { useShopCart, useShopProfile } from "./shop";

// TNK-124 phase F: filter state lives in the URL so refresh + browser
// back/forward + shared links all keep the buyer's filter selection.
// validateSearch is the gatekeeper — malformed query params are
// silently dropped so a hand-edited link can't break the page.
//
// TNK-124 phase F follow-up: the URL is now a *write-only mirror* —
// the filter state lives in local React state and we sync it to the
// browser's URL bar via history.replaceState on every change instead
// of routing through TanStack Router's navigate. The router's
// scroll-restoration was unwinnable in our layout (kept yanking the
// buyer to ~75px past the header on every pill click), even with
// resetScroll: false. validateSearch still hydrates initial state on
// page load + shared links.
export const Route = createFileRoute("/shop/inventory")({
  validateSearch: (search: Record<string, unknown>): ShopUrlSearch =>
    parseShopUrlSearch(search),
  component: ShopInventory,
});

/** 250ms debounce on the search input so each keystroke doesn't fire a
 *  full feed refetch. Filter pills and sort selection update
 *  synchronously — the perceived latency there is in the network, not
 *  the input. */
const SEARCH_DEBOUNCE_MS = 250;

function ShopInventory() {
  const profile = useShopProfile();
  const cart = useShopCart();
  // Initial URL state hydrates on mount + on shared-link landings;
  // subsequent mutations bypass the router (see filter state below).
  const initialUrlSearch = Route.useSearch();

  // Server-aggregated filter options + totals (TNK-124 phase E + G
  // follow-up). Fetched once on mount — covers ALL eligible inventory,
  // not just the loaded pages, so the sidebar pills + the hero's
  // "of N" / "$min-$max" badges never go stale as the buyer scrolls
  // or filters.
  const [facets, setFacets] = useState<StorefrontFacetsResponse | null>(null);
  // TNK-117: sold count for the trust-signal pill on the hero. One-shot
  // fetch alongside facets — fine for the public storefront load.
  const [soldCount, setSoldCount] = useState(0);
  // TNK-208: latest sold / latest added card names for the ticker items.
  const [latestSold, setLatestSold] = useState<string | null>(null);
  const [latestAdded, setLatestAdded] = useState<string | null>(null);
  // TNK-212 phase 2a: listed bundles, pinned to the front of the grid (hidden
  // when a card facet/search is active — see cardFacetActive below).
  const [bundles, setBundles] = useState<StorefrontBundle[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out = await fetchStorefrontFacets().catch(() => null);
      if (cancelled || !out) return;
      setFacets(out);
    })();
    void (async () => {
      const out = await fetchStorefrontBundles().catch(() => null);
      if (cancelled || !out) return;
      setBundles(out);
    })();
    void (async () => {
      const out = await fetchStorefrontSold({ limit: 1 }).catch(() => null);
      if (cancelled || !out) return;
      setSoldCount(out.total);
      setLatestSold(out.cards[0]?.name ?? null);
    })();
    void (async () => {
      const out = await fetchStorefrontFeed({ limit: 1, sort: "newest" }).catch(
        () => null,
      );
      if (cancelled || !out) return;
      setLatestAdded(out.cards[0]?.name ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo<FilterOptions | null>(
    () =>
      facets
        ? {
            games: facets.games,
            // Server facets return generic strings; cast to the visual-
            // rarity enum that ShopFilters expects (the api derives the
            // same bucket vocabulary in lib/storefrontFacets.ts).
            rarities: facets.rarities as ShopRarity[],
            types: facets.types,
            elements: facets.elements,
            pokemonEnergies: facets.pokemonEnergies,
            magicCardTypes: facets.magicCardTypes,
            colors: facets.colors,
            conditions: facets.conditions,
            priceCap: facets.priceCap,
          }
        : null,
    [facets],
  );

  // Filter state lives in local React state — the URL is a write-only
  // mirror updated via history.replaceState on each change. Bypassing
  // the router means no scroll restoration, no validateSearch
  // round-trip, no re-render of the route component, just instant
  // filter changes that flow through to the feed.
  const priceCap = options?.priceCap ?? 1000;
  const [filters, setFiltersRaw] = useState<ShopFilterState>(() =>
    searchToFilters(initialUrlSearch, priceCap),
  );
  // Hydrate priceMax from facets once they land — initial state used
  // a placeholder priceCap of 1000.
  useEffect(() => {
    if (!options) return;
    setFiltersRaw((prev) =>
      prev.priceMax === 0 || prev.priceMax >= options.priceCap
        ? { ...prev, priceMax: options.priceCap }
        : prev,
    );
  }, [options]);

  // Sync local filter state to the URL bar so the buyer can refresh /
  // share / bookmark. Skip the initial mount since the URL already
  // matches. We deliberately call the History.prototype's *unpatched*
  // replaceState — TanStack Router wraps the live `window.history.
  // replaceState` to drive its own scroll restoration, and going
  // through the wrapper here was yanking the buyer back to the top on
  // every pill click. Calling the prototype's method directly updates
  // the address bar without touching the router's state.
  const isFirstSync = useRef(true);
  useEffect(() => {
    if (isFirstSync.current) {
      isFirstSync.current = false;
      return;
    }
    const params = new URLSearchParams();
    const mirror = filtersToSearch(filters, priceCap);
    for (const [k, v] of Object.entries(mirror)) {
      if (v !== undefined && v !== "") params.set(k, String(v));
    }
    const qs = params.toString();
    const url = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;
    const nativeReplaceState = History.prototype.replaceState;
    nativeReplaceState.call(window.history, window.history.state, "", url);
  }, [filters, priceCap]);

  // ShopFilters' onChange contract is unchanged.
  const setFilters = (next: ShopFilterState): void => setFiltersRaw(next);

  // Search input gets its own immediate-value local state. Each
  // keystroke updates the input synchronously; a 250ms debounce
  // promotes the trimmed value into `filters.search`, which is what
  // reaches the feed.
  const [searchInput, setSearchInput] = useState<string>(filters.search);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchInputChange = (value: string): void => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFiltersRaw((prev) => ({ ...prev, search: value.trim() }));
    }, SEARCH_DEBOUNCE_MS);
  };

  const sort: StorefrontSort = filters.sortBy;
  const serverFilters = useMemo<StorefrontFeedFilters>(
    () => ({
      games: filters.game,
      rarities: filters.rarity,
      types: filters.type,
      elements: filters.element,
      colors: filters.color,
      conditions: filters.condition,
      priceMin: filters.priceMin,
      // Don't pass priceMax when it's at the cap (server treats >= cap
      // as "no ceiling" anyway) or when facets haven't loaded yet
      // (avoids an extra refetch when the placeholder default snaps to
      // the real cap on facets arrival). Keeps the URL clean for phase F.
      priceMax:
        !options || filters.priceMax >= options.priceCap ? 0 : filters.priceMax,
      foilOnly: filters.foilOnly,
    }),
    [filters, options],
  );

  const feed = useStorefrontFeed({
    // filters.search is the debounced value — instant typing into the
    // search input flows through `searchInput` first, then promotes
    // here after a 250ms idle so the feed doesn't fire per keystroke.
    query: filters.search,
    sort,
    filters: serverFilters,
  });

  const cards = feed.state.phase === "ready" ? feed.state.cards : [];
  const filteredTotal = feed.state.phase === "ready" ? feed.state.total : 0;
  const hasMore = feed.state.phase === "ready" && feed.state.hasMore;

  // TNK-212 phase 2a — the Bundles rail hides the moment a single-card facet
  // or search is active (operator chose hide-under-filters). Game + sort are
  // NOT card-attribute facets here, so by-game / sorted views still show it.
  const cardFacetActive =
    filters.search.trim().length > 0 ||
    filters.rarity.length > 0 ||
    filters.type.length > 0 ||
    filters.element.length > 0 ||
    filters.color.length > 0 ||
    filters.condition.length > 0 ||
    filters.foilOnly ||
    filters.priceMin > 0 ||
    (options ? filters.priceMax < options.priceCap : false);

  // Modal state (TNK-115 phase B2). Selected card id rather than the
  // full card so prev/next stay in sync with the loaded slice when
  // filters change mid-modal (rare, but the cleanest invariant).
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const openCard = useMemo(
    () =>
      openCardId ? (cards.find((c) => c.id === openCardId) ?? null) : null,
    [cards, openCardId],
  );

  // Hero price range mirrors the FULL eligible inventory (from facets),
  // not the loaded subset. Otherwise the badge reads "0.12 – 8.00" the
  // moment 60 cards are loaded even though the store actually carries
  // up to $50 elsewhere. Falls back to zero only while facets are
  // in-flight; the ShopHero hides the badge when totalCount is 0.
  const heroPriceMin = facets?.priceMin ?? 0;
  const heroPriceMax = facets?.priceMax ?? 0;

  // TNK-208 — build the enabled ticker items from operator config + feed data.
  // Null config = default (cards-shipped only, fade), preserving prior behavior.
  const tickerCfg = profile.tickerConfig ?? { shipped: true };
  const tickerItems: string[] = [];
  if (tickerCfg.shipped && soldCount > 0)
    tickerItems.push(
      `✓ ${soldCount.toLocaleString()} card${soldCount === 1 ? "" : "s"} shipped`,
    );
  if (tickerCfg.available && (facets?.total ?? 0) > 0)
    tickerItems.push(
      `${(facets?.total ?? 0).toLocaleString()} cards available`,
    );
  if (tickerCfg.pricesFrom && heroPriceMin > 0)
    tickerItems.push(`Singles from $${heroPriceMin.toFixed(2)}`);
  if (tickerCfg.justSold && latestSold)
    tickerItems.push(`Just sold: ${latestSold}`);
  if (tickerCfg.recentlyAdded && latestAdded)
    tickerItems.push(`Just added: ${latestAdded}`);
  if (tickerCfg.games && (facets?.games?.length ?? 0) > 0)
    tickerItems.push(
      facets!.games.length <= 2
        ? facets!.games.join(" & ")
        : `${facets!.games.length} games`,
    );
  (tickerCfg.custom ?? []).forEach((m) => {
    const t = m.trim();
    if (t) tickerItems.push(t);
  });

  // Infinite-scroll sentinel — when this empty div enters the viewport
  // the feed loads the next page. rootMargin 480px so the next fetch
  // kicks off before the buyer actually hits the bottom of the grid.
  //
  // `feed.state.phase` is in the deps so the effect re-runs when the
  // first page lands. The early "Loading…" return below means the
  // sentinel ref is null on the initial render, so without this dep
  // the observer would never attach.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          feed.loadMore();
        }
      },
      { rootMargin: "480px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [feed.loadMore, feed.state.phase]);

  // Auto-fill follow-up: IntersectionObserver only refires on STATE
  // CHANGE — if the sentinel stays in view after a page load (e.g. the
  // grid is shorter than viewport+rootMargin), the observer doesn't
  // refire and the buyer sees only the first 60 cards. This effect
  // checks the bottom proximity after every cards-length change and
  // recurses the loader until the grid extends well past the buyer's
  // current scroll position or the feed exhausts.
  //
  // Threshold = 1.5 × viewport height: aggressive enough that a buyer
  // who scrolls anywhere near the bottom keeps getting fresh pages
  // (so PageDown / End / fast-scroll to the visual end cascades
  // through multiple loads without needing per-scroll triggers), but
  // conservative enough that a casual mid-scroll doesn't auto-load
  // ahead. requestAnimationFrame waits for layout to settle so the
  // scrollHeight read reflects the new cards.
  useEffect(() => {
    if (!hasMore) return;
    if (feed.loadingMore) return;
    const raf = requestAnimationFrame(() => {
      const distance =
        document.documentElement.scrollHeight -
        (window.scrollY + window.innerHeight);
      if (distance < window.innerHeight * 1.5) {
        feed.loadMore();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [cards.length, hasMore, feed.loadingMore, feed.loadMore]);

  // Backup loader for burst scrolls (PageDown / End / mouse wheel that
  // overshoots). IntersectionObserver only fires on state transitions
  // — a single PageDown can carry the buyer past the sentinel's
  // "intersecting" window in one frame, so the observer's entry event
  // never fires for that interval and the next loadMore goes
  // unrequested. This scroll listener checks the bottom proximity on
  // every scroll event and fires loadMore when the buyer is within
  // 1.5 × viewport of the end of the document. Cheap; scroll events
  // are already throttled by the browser to once per frame.
  useEffect(() => {
    const onScroll = (): void => {
      if (!hasMore || feed.loadingMore) return;
      const distance =
        document.documentElement.scrollHeight -
        (window.scrollY + window.innerHeight);
      if (distance < window.innerHeight * 1.5) {
        feed.loadMore();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, feed.loadingMore, feed.loadMore]);

  if (feed.state.phase === "loading" && cards.length === 0) {
    return <p className="py-12 text-center text-muted-foreground">Loading…</p>;
  }
  if (feed.state.phase === "error") {
    return (
      <p className="py-12 text-center text-destructive">
        Couldn’t load the inventory.
      </p>
    );
  }

  // ShopFilters needs FilterOptions to render. Until the facets fetch
  // lands, we render a minimal placeholder shape — the grid below is
  // already populating so the page doesn't read empty.
  const sidebarOptions: FilterOptions = options ?? {
    games: [],
    rarities: [],
    types: [],
    elements: [],
    pokemonEnergies: [],
    magicCardTypes: [],
    colors: [],
    conditions: [],
    priceCap: 1000,
  };

  return (
    <>
      {/* TNK-208: buyer-confidence ticker — flush strip between the menu and the
          hero, rotating the operator's enabled items (renders nothing if none). */}
      <Ticker
        items={tickerItems}
        motion={tickerCfg.motion ?? "fade"}
        speed={tickerCfg.speed ?? "medium"}
        gap={tickerCfg.gap ?? 48}
      />
      <ShopHero
        storeName={profile.storeName || "Card Store"}
        heroRef={profile.heroRef}
        heroHeight={profile.heroHeight}
        heroOpacity={profile.heroOpacity}
        heroFit={profile.heroFit}
        filteredCount={filteredTotal}
        totalCount={facets?.total ?? filteredTotal}
        priceMin={heroPriceMin}
        priceMax={heroPriceMax}
        games={sidebarOptions.games}
        activeGame={filters.game}
        onGameSelect={(game) => setFilters({ ...filters, game })}
      />

      {/* Sticky search — pinned to the top of the viewport so the
          buyer can refine without scrolling back up. TNK-124 phase E:
          bound to filters.search; the debounced value reaches the
          server via useStorefrontFeed below. TNK-124 phase G follow-
          up: inline Cart button so the buyer can move to checkout
          from any scroll depth without bouncing to the top first. */}
      <div className="sticky top-0 z-30 -mx-4 mb-4 border-b border-border/40 bg-background/85 px-4 pb-3 pt-2 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              placeholder="Search cards, sets…"
              className="w-full rounded-md border border-border bg-card py-2 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() => cart.openDrawer()}
            aria-label={`Open cart (${cart.count} item${cart.count === 1 ? "" : "s"})`}
            className="relative inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus:border-primary focus:outline-none"
          >
            <ShoppingCart className="h-4 w-4" />
            {cart.count > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">
                {cart.count}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <ShopFilters
          filters={filters}
          options={sidebarOptions}
          onChange={setFilters}
          totalCount={cards.length}
          filteredCount={cards.length}
        />
        <div className="relative min-w-0 flex-1">
          {/* Stale-while-revalidate: dim the grid slightly during a
              first-page refetch (filter/sort/query change) instead of
              blanking it. The previous results stay readable so the
              buyer can keep skimming; cards swap in when the new page
              lands. transition-opacity keeps the effect smooth. */}
          <div
            className={
              feed.refetching
                ? "opacity-60 transition-opacity duration-150"
                : "opacity-100 transition-opacity duration-150"
            }
          >
            <CardGrid
              cards={cards}
              cartIds={cart.ids}
              onAddToCart={(c) => cart.add(c.id)}
              onOpenCard={(c) => setOpenCardId(c.id)}
              bundles={cardFacetActive ? [] : bundles}
            />
          </div>
          {feed.refetching && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden">
              <div
                className="h-full w-1/3 animate-pulse"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(201,168,76,0.8), transparent)",
                }}
              />
            </div>
          )}
          {/* Sentinel + footer indicator. The sentinel is what the
              IntersectionObserver watches; the indicator is for the
              buyer. */}
          <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
          {feed.loadingMore && (
            <p className="py-6 text-center text-xs text-muted-foreground">
              Loading more cards…
            </p>
          )}
          {feed.state.phase === "ready" &&
            !feed.state.hasMore &&
            cards.length > 0 && (
              <p className="py-6 text-center text-xs text-muted-foreground">
                {/* TNK-116: drop the catalog-wide card count — operator
                    metric, not buyer signal. */}
                End of catalog
              </p>
            )}
          {feed.state.phase === "ready" && cards.length === 0 && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No cards match your filters.
            </p>
          )}
        </div>
      </div>

      <CardDetailModal
        card={openCard}
        cards={cards}
        reservedIds={cart.ids}
        onReserve={(id) => cart.add(id)}
        onRemove={(id) => cart.remove(id)}
        onClose={() => setOpenCardId(null)}
        onNavigate={(id) => setOpenCardId(id)}
      />

      <BackToTop />
    </>
  );
}
